import { defineAsyncComponent } from 'vue';
import { GlButton, GlEmptyState, GlTooltipDirective, GlSprintf, GlLink, GlAlert, GlSkeletonLoader } from '@gitlab/ui';
import { get } from 'lodash-es';
import getContainerRepositoriesQuery from 'shared_queries/container_registry/get_container_repositories.query.graphql';
import { createAlert } from '~/alert';
import { NAMESPACE_GROUP, NAMESPACE_PROJECT } from '~/issues/constants';
import { fetchPolicies } from '~/lib/graphql';
import Tracking from '~/tracking';
import PersistedPagination from '~/packages_and_registries/shared/components/persisted_pagination.vue';
import PersistedSearch from '~/packages_and_registries/shared/components/persisted_search.vue';
import { FILTERED_SEARCH_TERM } from '~/vue_shared/components/filtered_search_bar/constants';
import { getPageParams, getNextPageParams, getPreviousPageParams } from '~/packages_and_registries/shared/utils';
import { helpPagePath } from '~/helpers/help_page_helper';
import DeleteImage from '../components/delete_image.vue';
import RegistryHeader from '../components/list_page/registry_header.vue';
import DeleteModal from '../components/delete_modal.vue';
import { DELETE_IMAGE_SUCCESS_MESSAGE, DELETE_IMAGE_ERROR_MESSAGE, CONNECTION_ERROR_TITLE, CONNECTION_ERROR_MESSAGE, EMPTY_RESULT_TITLE, EMPTY_RESULT_MESSAGE, GRAPHQL_PAGE_SIZE, GRAPHQL_PAGE_SIZE_METADATA_ENABLED, FETCH_IMAGES_LIST_ERROR_MESSAGE, SORT_FIELDS, SETTINGS_TEXT } from '../constants/index';
import getContainerRepositoriesDetails from '../graphql/queries/get_container_repositories_details.query.graphql';
export default {
    name: 'RegistryListPage',
    components: {
        GlButton,
        GlEmptyState,
        ProjectEmptyState: defineAsyncComponent(()=>import(/* webpackChunkName: 'container_registry_components' */ '../components/list_page/project_empty_state.vue')),
        GroupEmptyState: defineAsyncComponent(()=>import(/* webpackChunkName: 'container_registry_components' */ '../components/list_page/group_empty_state.vue')),
        ImageList: defineAsyncComponent(()=>import(/* webpackChunkName: 'container_registry_components' */ '../components/list_page/image_list.vue')),
        CliCommands: defineAsyncComponent(()=>import(/* webpackChunkName: 'container_registry_components' */ '~/packages_and_registries/shared/components/cli_commands.vue')),
        DeleteModal,
        GlSprintf,
        GlLink,
        GlAlert,
        GlSkeletonLoader,
        RegistryHeader,
        DeleteImage,
        PersistedPagination,
        PersistedSearch
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        Tracking.mixin()
    ],
    inject: [
        'config',
        'dockerBuildCommand',
        'dockerPushCommand',
        'dockerLoginCommand'
    ],
    loader: {
        repeat: 10,
        width: 1000,
        height: 40
    },
    i18n: {
        CONNECTION_ERROR_TITLE,
        CONNECTION_ERROR_MESSAGE,
        EMPTY_RESULT_TITLE,
        EMPTY_RESULT_MESSAGE,
        SETTINGS_TEXT
    },
    searchConfig: SORT_FIELDS,
    apollo: {
        baseImages: {
            skip () {
                return !this.fetchBaseQuery;
            },
            query: getContainerRepositoriesQuery,
            fetchPolicy: fetchPolicies.CACHE_AND_NETWORK,
            variables () {
                return this.queryVariables;
            },
            update (data) {
                return data[this.graphqlResource]?.containerRepositories?.nodes ?? [];
            },
            result ({ data }) {
                if (!data) {
                    return;
                }
                this.pageInfo = data[this.graphqlResource]?.containerRepositories?.pageInfo;
                this.containerRepositoriesCount = data[this.graphqlResource]?.containerRepositoriesCount;
            },
            error () {
                createAlert({
                    message: FETCH_IMAGES_LIST_ERROR_MESSAGE
                });
            }
        },
        additionalDetails: {
            skip () {
                return !this.fetchAdditionalDetails;
            },
            query: getContainerRepositoriesDetails,
            fetchPolicy: fetchPolicies.CACHE_AND_NETWORK,
            variables () {
                return this.queryVariables;
            },
            update (data) {
                return data[this.graphqlResource]?.containerRepositories?.nodes ?? [];
            },
            error () {
                createAlert({
                    message: FETCH_IMAGES_LIST_ERROR_MESSAGE
                });
            }
        }
    },
    data () {
        return {
            baseImages: [],
            additionalDetails: [],
            pageInfo: {},
            containerRepositoriesCount: 0,
            itemToDelete: {},
            deleteAlertType: null,
            deleteAlertMessage: null,
            deleteImageErrorMessages: [],
            sorting: null,
            name: null,
            mutationLoading: false,
            fetchBaseQuery: false,
            fetchAdditionalDetails: false,
            pageParams: {}
        };
    },
    computed: {
        images () {
            if (this.baseImages) {
                return this.baseImages.map((image, index)=>({
                        ...image,
                        ...get(this.additionalDetails, index, {})
                    }));
            }
            return [];
        },
        itemsToBeDeleted () {
            return this.itemToDelete?.id ? [
                this.itemToDelete
            ] : [];
        },
        graphqlResource () {
            return this.config.isGroupPage ? NAMESPACE_GROUP : NAMESPACE_PROJECT;
        },
        pageSize () {
            return this.config.isMetadataDatabaseEnabled ? GRAPHQL_PAGE_SIZE_METADATA_ENABLED : GRAPHQL_PAGE_SIZE;
        },
        queryVariables () {
            return {
                name: this.name,
                sort: this.sorting,
                fullPath: this.config.isGroupPage ? this.config.groupPath : this.config.projectPath,
                isGroupPage: this.config.isGroupPage,
                first: this.pageSize,
                ...this.pageParams
            };
        },
        // eslint-disable-next-line vue/no-unused-properties -- tracking() is required by Tracking mixin.
        tracking () {
            return {
                label: 'registry_repository_delete'
            };
        },
        isLoading () {
            return this.$apollo.queries.baseImages.loading || this.mutationLoading;
        },
        showCommands () {
            return Boolean(!this.isLoading && !this.config?.isGroupPage && this.images?.length);
        },
        showDeleteAlert () {
            return this.deleteAlertType && this.itemToDelete?.path;
        },
        showConnectionError () {
            return this.config.connectionError || this.config.invalidPathError;
        }
    },
    methods: {
        deleteImage (item) {
            this.track('click_button');
            this.itemToDelete = item;
            this.$refs.deleteModal.show();
        },
        dismissDeleteAlert () {
            this.setDeleteAlert(null, null);
            this.itemToDelete = {};
        },
        fetchNextPage () {
            this.pageParams = getNextPageParams(this.pageInfo?.endCursor, this.pageSize);
        },
        fetchPreviousPage () {
            this.pageParams = getPreviousPageParams(this.pageInfo?.startCursor, this.pageSize);
        },
        startDelete () {
            this.track('confirm_delete');
            this.mutationLoading = true;
        },
        handleSearchUpdate ({ sort, filters, pageInfo }) {
            this.pageParams = getPageParams(pageInfo, this.pageSize);
            this.sorting = sort;
            const search = filters.find((i)=>i.type === FILTERED_SEARCH_TERM);
            this.name = search?.value?.data;
            if (!this.fetchBaseQuery && !this.fetchAdditionalDetails) {
                // If the two graphql calls - which are not batched - resolve together we will have a race
                // condition when apollo sets the cache, with this we give the 'base' call an headstart
                this.fetchBaseQuery = true;
                setTimeout(()=>{
                    this.fetchAdditionalDetails = true;
                }, 200);
            }
        },
        setDeleteAlert (alertType, alertMessage) {
            this.deleteAlertType = alertType;
            this.deleteAlertMessage = alertMessage;
        },
        setDeleteErrorMessages (deleteErrorMessages = []) {
            this.deleteImageErrorMessages = deleteErrorMessages ?? [];
        },
        handleDeleteImageSuccess () {
            this.setDeleteAlert('success', DELETE_IMAGE_SUCCESS_MESSAGE);
        },
        handleDeleteImageError (errors = []) {
            this.setDeleteAlert('danger', DELETE_IMAGE_ERROR_MESSAGE);
            const errorMessages = (errors || []).map((error)=>{
                return error?.message || String(error);
            }).filter(Boolean) || [];
            this.setDeleteErrorMessages(errorMessages);
        }
    },
    dockerConnectionErrorHelpUrl: helpPagePath('user/packages/container_registry/troubleshoot_container_registry', {
        anchor: 'docker-connection-error'
    })
};
